<template>
  <div class="instrument-ledger">
    <div class="ledger-intro">
      <strong>仪器设备台账</strong>：对齐 <code>GSYN-BG-(GL)-043</code> 检定台账全字段；数据来自 <code>labe/</code> 三份源合并入库 <code>yn_lab_instrument</code>。
      「重新导入」会删除旧表并按最新结构重建后再灌数。
    </div>

    <div class="ledger-toolbar">
      <el-input
        v-model="keyword"
        clearable
        placeholder="搜索自编号 / 名称 / 证书号 / 地点 / 厂商"
        size="small"
        class="search-input"
        @keyup.enter.native="loadData(1)"
        @clear="loadData(1)"
      />
      <el-select v-model="deviceType" clearable placeholder="设备类型" size="small" class="filter-select" @change="loadData(1)">
        <el-option label="实验室台账" value="lab" />
        <el-option label="现场/房间台账" value="field" />
        <el-option label="温湿度计" value="hygro" />
        <el-option label="仅检定证书" value="cert_only" />
      </el-select>
      <el-button size="small" type="primary" :loading="loading" @click="loadData(1)">查询</el-button>
      <el-button size="small" type="warning" plain :loading="importing" @click="reimport">重新导入</el-button>
      <span v-if="stats.total" class="stats-text">
        共 {{ stats.total }} 条 · 表 yn_lab_instrument
      </span>
    </div>

    <el-table
      v-loading="loading"
      :data="rows"
      border
      stripe
      size="small"
      class="ledger-table"
      height="calc(100vh - 180px)"
    >
      <el-table-column label="序号" width="56" align="center" fixed>
        <template slot-scope="{ $index }">{{ rowIndex($index) }}</template>
      </el-table-column>
      <el-table-column prop="sb_code" label="自编号" width="100" fixed />
      <el-table-column label="显示名称" min-width="160" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'display_name') }}</template>
      </el-table-column>
      <el-table-column label="设备名称" min-width="120" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'sb_name', 'cert_name', 'hygro_name') }}</template>
      </el-table-column>
      <el-table-column label="型号" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'sb_model', 'cert_model', 'hygro_meter_code') }}</template>
      </el-table-column>
      <el-table-column label="出厂编号" width="120" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'sb_factory_no', 'cert_factory_no') }}</template>
      </el-table-column>
      <el-table-column label="生产厂商" min-width="130" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'sb_manufacturer', 'cert_manufacturer') }}</template>
      </el-table-column>
      <el-table-column label="不确定度/参数" min-width="160" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatCell(row.cert_uncertainty) }}</template>
      </el-table-column>
      <el-table-column label="检定结论" width="90" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatCell(row.cert_conclusion) }}</template>
      </el-table-column>
      <el-table-column label="数量" width="56" align="center">
        <template slot-scope="{ row }">{{ formatCell(row.cert_qty) }}</template>
      </el-table-column>
      <el-table-column label="存放地点" width="100" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ cellName(row, 'sb_location', 'room_name') }}</template>
      </el-table-column>
      <el-table-column label="房间号" width="72">
        <template slot-scope="{ row }">{{ cellName(row, 'sb_room_no', 'hygro_room_no') }}</template>
      </el-table-column>
      <el-table-column label="保管人" width="72">
        <template slot-scope="{ row }">{{ formatCell(row.sb_custodian) }}</template>
      </el-table-column>
      <el-table-column label="检定/校准" width="80">
        <template slot-scope="{ row }">{{ formatCell(row.cert_trace_type) }}</template>
      </el-table-column>
      <el-table-column label="本次检定" width="100">
        <template slot-scope="{ row }">{{ formatCell(row.cert_date_last) }}</template>
      </el-table-column>
      <el-table-column label="有效期至" width="100">
        <template slot-scope="{ row }">{{ formatCell(row.cert_expiry) }}</template>
      </el-table-column>
      <el-table-column label="证书编号" width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatCell(row.cert_no) }}</template>
      </el-table-column>
      <el-table-column label="检定单位" min-width="140" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatCell(row.cert_org) }}</template>
      </el-table-column>
      <el-table-column label="分类" width="72">
        <template slot-scope="{ row }">{{ formatCell(row.sb_category) }}</template>
      </el-table-column>
      <el-table-column label="类型" width="72">
        <template slot-scope="{ row }">{{ deviceTypeLabel(row.device_type) }}</template>
      </el-table-column>
      <el-table-column label="来源表" width="110" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatCell(row.ledger_sheet) || sourceLabel(row) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="168" fixed="right" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openView(row)">查看</el-button>
          <el-button type="text" size="small" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" size="small" class="btn-del" @click="removeRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :total="total"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[20, 50, 100, 200]"
        @current-change="loadData"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog
      :title="dialogMode === 'edit' ? '编辑设备台账' : '查看设备台账'"
      :visible.sync="dialogVisible"
      width="760px"
      top="5vh"
      :close-on-click-modal="false"
      @closed="onDialogClosed"
    >
      <div v-loading="dialogLoading">
        <template v-if="dialogMode === 'view' && formRow">
          <div v-for="sec in formSections" :key="sec.title" class="form-section">
            <div class="section-title">{{ sec.title }}</div>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item
                v-for="f in sec.fields"
                :key="f.prop"
                :label="f.label"
                :span="f.span || 1"
              >
                {{ formatCell(formRow[f.prop]) }}
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </template>
        <el-form v-else-if="formRow" ref="editForm" :model="formRow" label-width="108px" size="small">
          <div v-for="sec in formSections" :key="sec.title" class="form-section">
            <div class="section-title">{{ sec.title }}</div>
            <el-row :gutter="12">
              <el-col v-for="f in sec.fields" :key="f.prop" :span="f.span ? 24 : 12">
                <el-form-item :label="f.label">
                  <el-input
                    v-if="f.prop === 'sb_code'"
                    v-model="formRow[f.prop]"
                    disabled
                  />
                  <el-select
                    v-else-if="f.prop === 'device_type'"
                    v-model="formRow[f.prop]"
                    style="width:100%"
                  >
                    <el-option label="实验室台账" value="lab" />
                    <el-option label="按房间台账" value="field" />
                    <el-option label="温湿度计" value="hygro" />
                    <el-option label="仅检定证书" value="cert_only" />
                  </el-select>
                  <el-input
                    v-else-if="f.type === 'textarea'"
                    v-model="formRow[f.prop]"
                    type="textarea"
                    :rows="2"
                  />
                  <el-input v-else v-model="formRow[f.prop]" clearable />
                </el-form-item>
              </el-col>
            </el-row>
          </div>
        </el-form>
      </div>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">关闭</el-button>
        <el-button v-if="dialogMode === 'view'" size="small" type="primary" @click="switchToEdit">编辑</el-button>
        <el-button v-if="dialogMode === 'edit'" size="small" type="primary" :loading="saving" @click="saveRow">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/utils/api'

const FORM_SECTIONS = [
  {
    title: '基本信息',
    fields: [
      { prop: 'sb_code', label: '自编号' },
      { prop: 'display_name', label: '显示名称', span: 2 },
      { prop: 'device_type', label: '设备类型' },
      { prop: 'sb_category', label: '分类' },
      { prop: 'ledger_sheet', label: '台账来源', span: 2 }
    ]
  },
  {
    title: '设备台账（xlsx）',
    fields: [
      { prop: 'sb_name', label: '设备名称' },
      { prop: 'sb_model', label: '型号' },
      { prop: 'sb_manufacturer', label: '生产厂商' },
      { prop: 'sb_factory_no', label: '出厂编号' },
      { prop: 'sb_custodian', label: '保管人' },
      { prop: 'sb_location', label: '存放地点' },
      { prop: 'sb_room_no', label: '房间号' },
      { prop: 'room_name', label: '房间名称' }
    ]
  },
  {
    title: '温湿度计',
    fields: [
      { prop: 'hygro_room_no', label: '房间号' },
      { prop: 'hygro_meter_code', label: '仪表编号' },
      { prop: 'hygro_name', label: '名称' },
      { prop: 'hygro_parent_code', label: '关联YN编号' }
    ]
  },
  {
    title: '检定校准（docx）',
    fields: [
      { prop: 'cert_name', label: '仪器名称' },
      { prop: 'cert_model', label: '型号' },
      { prop: 'cert_factory_no', label: '出厂编号' },
      { prop: 'cert_manufacturer', label: '生产厂商' },
      { prop: 'cert_date_last', label: '本次检定日期' },
      { prop: 'cert_expiry', label: '下次校准日期' },
      { prop: 'cert_trace_type', label: '检定/校准' },
      { prop: 'cert_no', label: '证书编号' },
      { prop: 'cert_org', label: '检定单位', span: 2 },
      { prop: 'cert_uncertainty', label: '不确定度', span: 2, type: 'textarea' },
      { prop: 'cert_conclusion', label: '检定结论' },
      { prop: 'cert_qty', label: '数量' }
    ]
  }
]

export default {
  name: 'InstrumentLedger',
  data() {
    return {
      keyword: '',
      deviceType: '',
      rows: [],
      total: 0,
      page: 1,
      pageSize: 50,
      loading: false,
      importing: false,
      stats: {},
      formSections: FORM_SECTIONS,
      dialogVisible: false,
      dialogMode: 'view',
      dialogLoading: false,
      saving: false,
      formRow: null
    }
  },
  mounted() {
    this.loadData(1)
  },
  methods: {
    deviceTypeLabel(v) {
      const map = { lab: '实验室', field: '现场/房间', hygro: '温湿度', cert_only: '仅证书' }
      return map[v] || v || '-'
    },
    formatCell(v) {
      if (v === null || v === undefined || v === '') return '—'
      const s = String(v).trim()
      if (s === '/' || s === '—' || s === '-') return '—'
      return s
    },
    cellName(row, ...keys) {
      for (const k of keys) {
        const v = row[k]
        if (v !== null && v !== undefined && String(v).trim() && String(v).trim() !== '/') {
          return String(v).trim()
        }
      }
      return '—'
    },
    sourceLabel(row) {
      const dt = row.device_type || ''
      if (dt === 'hygro') return '温湿度表'
      if (dt === 'cert_only') return '仅证书'
      if (row.cert_no && row.sb_location) return '台账+证书'
      if (row.cert_no) return '证书'
      return '台账'
    },
    onSizeChange() {
      this.loadData(1)
    },
    rowIndex(i) {
      return (this.page - 1) * this.pageSize + i + 1
    },
    async loadData(p) {
      this.page = p || this.page
      this.loading = true
      try {
        const res = await api.get('yn_tr_instruments_ledger', {
          page: this.page,
          page_size: this.pageSize,
          keyword: this.keyword,
          device_type: this.deviceType
        })
        const data = res.data || {}
        if (data.error) {
          this.$message.error(data.error)
          return
        }
        this.rows = data.rows || []
        this.total = data.total || 0
        this.stats = { total: data.total || 0 }
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async fetchRow(id) {
      const res = await api.get('yn_tr_instrument_get', { id })
      const data = res.data || {}
      if (data.error) throw new Error(data.error)
      return data.row || null
    },
    normalizeRow(row) {
      const out = { ...row }
      FORM_SECTIONS.forEach(sec => {
        sec.fields.forEach(f => {
          if (out[f.prop] === null || out[f.prop] === undefined) out[f.prop] = ''
        })
      })
      return out
    },
    async openView(row) {
      this.dialogMode = 'view'
      this.dialogVisible = true
      this.dialogLoading = true
      this.formRow = null
      try {
        this.formRow = this.normalizeRow(await this.fetchRow(row.id))
      } catch (e) {
        this.dialogVisible = false
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.dialogLoading = false
      }
    },
    async openEdit(row) {
      this.dialogMode = 'edit'
      this.dialogVisible = true
      this.dialogLoading = true
      this.formRow = null
      try {
        this.formRow = this.normalizeRow(await this.fetchRow(row.id))
      } catch (e) {
        this.dialogVisible = false
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.dialogLoading = false
      }
    },
    switchToEdit() {
      this.dialogMode = 'edit'
    },
    onDialogClosed() {
      this.formRow = null
      this.dialogMode = 'view'
    },
    async saveRow() {
      if (!this.formRow || !this.formRow.id) return
      this.saving = true
      try {
        const res = await api.post('yn_tr_instrument_save', {
          row: JSON.stringify(this.formRow)
        })
        const data = res.data || {}
        if (data.error) {
          this.$message.error(data.error)
          return
        }
        this.$message.success('已保存')
        this.formRow = this.normalizeRow(data.row || this.formRow)
        this.dialogMode = 'view'
        this.loadData(this.page)
      } catch (e) {
        this.$message.error((e && e.message) || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async removeRow(row) {
      try {
        await this.$confirm(
          `确定删除「${row.sb_code || row.display_name}」？删除后不可恢复（重新导入可恢复批量数据）。`,
          '删除确认',
          { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
        )
      } catch (e) {
        return
      }
      try {
        const res = await api.post('yn_tr_instrument_del', { id: row.id })
        const data = res.data || {}
        if (data.error) {
          this.$message.error(data.error)
          return
        }
        this.$message.success('已删除')
        if (this.dialogVisible && this.formRow && this.formRow.id === row.id) {
          this.dialogVisible = false
        }
        this.loadData(this.page)
      } catch (e) {
        this.$message.error((e && e.message) || '删除失败')
      }
    },
    async reimport() {
      try {
        await this.$confirm(
          '将从 config/lab_instruments_import.json（labe 台账合并结果）全量覆盖设备台账，是否继续？',
          '重新导入',
          { type: 'warning', confirmButtonText: '导入', cancelButtonText: '取消' }
        )
      } catch (e) {
        return
      }
      this.importing = true
      try {
        const res = await api.get('yn_tr_instruments_import')
        const data = res.data || {}
        if (data.error) {
          this.$message.error(data.error)
          return
        }
        this.$message.success(`导入成功：${data.imported || 0} 条`)
        this.loadData(1)
      } catch (e) {
        this.$message.error((e && e.message) || '导入失败')
      } finally {
        this.importing = false
      }
    }
  }
}
</script>

<style scoped>
.instrument-ledger {
  padding: 4px 0;
}
.ledger-intro {
  margin-bottom: 10px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.6;
  color: #606266;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}
.ledger-intro code {
  padding: 0 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 2px;
}
.ledger-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.search-input {
  width: 280px;
}
.filter-select {
  width: 140px;
}
.stats-text {
  margin-left: auto;
  font-size: 13px;
  color: #909399;
}
.ledger-table {
  width: 100%;
}
.btn-del {
  color: #f56c6c;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
.form-section {
  margin-bottom: 16px;
}
.section-title {
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
</style>
